// src/pages/ChangelogPage.jsx
//
// Public changelog + "what's coming" roadmap. Read-only (no fake
// voting). The suggestion form writes to the same `feedback`
// collection the floating bug button uses, so anything coaches send
// here lands in /admin/feedback alongside bug reports and emails Kurt.

import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { addDoc, collection, serverTimestamp } from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import { useAuth } from '@/context/AuthContext'
import toast from 'react-hot-toast'
import {
  ArrowLeftIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
  RocketLaunchIcon,
  PaperAirplaneIcon,
  CheckIcon,
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
} from '@heroicons/react/24/outline'

const HEAD_FONT = "'Barlow Condensed', sans-serif"

// ----------------------------------------------------------------
// Past updates, newest first
// ----------------------------------------------------------------
const pastUpdates = [
  {
    id: -23,
    version: 'v2.3.1',
    date: 'July 26, 2026',
    type: 'improvement',
    title: 'Repeat Weekly Is Easier to Find',
    description:
      'When you add a practice, game, or meeting, the "Repeat weekly" option now sits right under the date and time fields instead of hiding at the bottom of the form. Turn it on, pick your days, and Scrimly builds the whole run of events in one save, each one watched separately for weather and ready for attendance. It was always there; now you can actually find it.',
    tags: ['Schedule', 'Improvement'],
  },
  {
    id: -22,
    version: 'v2.3.0',
    date: 'July 26, 2026',
    type: 'major',
    title: 'SMS Replies Now Show in Your Main Inbox',
    description:
      'When a parent texts back, you now see it everywhere: the Messages badge counts it, and your main inbox shows a row for each team with new SMS replies. Tap the row to jump straight to that team\'s replies. Before, a text reply only appeared on that team\'s Messages page, so coaches with several teams could miss them. Nothing changes for parents; they keep texting like always.',
    tags: ['Messages', 'SMS', 'Bilingual', 'New Feature'],
  },
  {
    id: -21,
    version: 'v2.2.7',
    date: 'July 23, 2026',
    type: 'feature',
    title: 'New Teams Start Ready to Open',
    description:
      'Creating a team now sets up a complete free season for you automatically (named for the time of year, like "Summer 2026"), with dates and no fee. It arrives as a draft: one tap on "Open registration" and your link is live — nothing is shown to parents until you say so. Waivers are your call: none is included, and you can add your club\'s own (or start from a sample template) on the season page. You can rename the season, add a fee, or change anything anytime. The team page stays focused on your next step until you send your first message, and while you wait for your first family to join, your registration link stays one tap away.',
    tags: ['Onboarding', 'Seasons', 'Feature'],
  },
  {
    id: -20,
    version: 'v2.2.6',
    date: 'July 23, 2026',
    type: 'improvement',
    title: 'Clearer Send Receipts and No More Lost Drafts',
    description:
      'Message receipts now tell you exactly what happened: texts held back by nighttime quiet hours (no SMS between 10 PM and 7 AM) are shown as "held" with a reminder to resend in the morning, instead of being lumped in with failures. Messages that only partly delivered now show a "Partly sent" badge instead of a green checkmark. And the "Invite families from the roster" button on the share step now opens the roster in a new tab, so your drafted message stays right where you left it.',
    tags: ['Messages', 'SMS', 'Improvement'],
  },
  {
    id: -19,
    version: 'v2.2.5',
    date: 'July 22, 2026',
    type: 'fix',
    title: 'Season Dates Now Land on the Right Day',
    description:
      'Fixed an off-by-one bug where season dates could show a day earlier than what the coach entered (a Season Start of 7/27 displayed as 7/26). The fix covers every place these dates appear: the season detail page, season cards, registration pages, and email invites. Registration close dates and pricing tier windows now also stay open through the entire final day, so a "closes 7/31" season accepts registrations until midnight on the 31st instead of cutting off early.',
    tags: ['Seasons', 'Registration', 'Bug Fix'],
  },
  {
    id: -18,
    version: 'v2.2.4',
    date: 'May 21, 2026',
    type: 'fix',
    title: 'Prospect Players No Longer Get Invited by Mistake',
    description:
      "Parents of players marked \"Prospect\" (still trying out / under evaluation) can no longer be invited until you promote the player to Active. Before, sending an invite to a prospect told their family they'd \"been added to the roster\" — even if they hadn't made the team yet. Promote to Active first, then invite.",
    tags: ['Roster', 'Invites', 'Fix'],
  },
  {
    id: -17,
    version: 'v2.2.3',
    date: 'May 20, 2026',
    type: 'improvement',
    title: 'Profile Saves Automatically',
    description:
      'Your name and profile photo now save on their own — your name saves as soon as you tap out of the field, and a new photo saves the moment you pick it. No more "Save Changes" bar floating at the bottom of the page.',
    tags: ['Settings', 'Profile'],
  },
  {
    id: -16,
    version: 'v2.2.2',
    date: 'May 20, 2026',
    type: 'improvement',
    title: 'Notification Bell Fixed on iPad & Desktop',
    description:
      'The notification bell now shows reliably on tablets and desktop. Before, it was cramped against the logo on iPad in portrait and missing entirely on larger screens. It now sits in its own spot in the sidebar and opens cleanly.',
    tags: ['Notifications', 'Tablet', 'Fix'],
  },
  {
    id: -15,
    version: 'v2.2.1',
    date: 'May 20, 2026',
    type: 'improvement',
    title: 'Player Photos on the Roster',
    description:
      'The headshot a parent uploads during registration now shows on the teammates card — photo with the jersey number in the corner — instead of just a number circle. Players without a photo still show their jersey number, so nothing looks empty.',
    tags: ['Roster', 'Photos', 'Bilingual'],
  },
  {
    id: -14,
    version: 'v2.2.0',
    date: 'May 19, 2026',
    type: 'major',
    title: 'New Help & Support Page',
    description:
      'The Help & Support page is rebuilt: searchable, on-brand, fully bilingual, and finally accurate (no more outdated pricing or WhatsApp-only instructions). Best part — "Contact support" is now built in. Tell us what you need right in the app and it comes straight to us with the context to help, no email gymnastics. We reply within one business day.',
    tags: ['Support', 'Bilingual', 'New Feature'],
  },
  {
    id: -13,
    version: 'v2.1.4',
    date: 'May 19, 2026',
    type: 'improvement',
    title: 'Chat Composer + Notification Bell Polish',
    description:
      'Fixed the chat message box getting hidden behind the bottom navigation bar on phones — you can now always see what you\'re typing and the send button. The notification bell dropdown also got the lime/pitch treatment: unread items show a lime dot, and the Mark-read / Clear actions match the rest of the app.',
    tags: ['Chat', 'Notifications', 'Mobile', 'Fix'],
  },
  {
    id: -12,
    version: 'v2.1.3',
    date: 'May 19, 2026',
    type: 'improvement',
    title: 'Notifications Page Refresh',
    description:
      'The notifications page — where you land when you tap a push — got a full redesign in the app\'s lime/pitch style. Unread items now stand out with a lime dot and tint, filters tuck behind a button so the toolbar isn\'t cramped on mobile, bulk actions slide in only when you select something, and every row has bigger, easier-to-tap controls.',
    tags: ['Notifications', 'Mobile', 'Design'],
  },
  {
    id: -11,
    version: 'v2.1.2',
    date: 'May 19, 2026',
    type: 'improvement',
    title: 'Financial Reports — Mobile Refresh',
    description:
      'The Financial Reports page now reads like a real mobile app instead of a desktop table squeezed onto a phone. Each registration shows up as a card with the player name front-and-center, status pill in the top right, amount in bold, and a thumb-friendly "Send payment reminder" button on the rows that need one. Header, season picker, filters, and search all stack cleanly on small screens. Desktop layout is unchanged.',
    tags: ['Financial', 'Mobile', 'Design'],
  },
  {
    id: -10,
    version: 'v2.1.1',
    date: 'May 19, 2026',
    type: 'improvement',
    title: 'Cleared Notifications Stay Cleared',
    description:
      'Tapping "Clear" in the notification bell dropdown used to hide notifications locally but never actually archive them on the server — so closing and reopening the app brought them all back. Now they\'re properly archived and stay gone. Same fix applies to the bell on web and inside the Android app.',
    tags: ['Notifications', 'Fix'],
  },
  {
    id: -9,
    version: 'v2.1.0',
    date: 'May 19, 2026',
    type: 'major',
    title: 'Push Notifications Live on Android',
    description:
      'Coach broadcasts, chat messages, and game-day alerts now land as banner notifications on Android phones with the installed Scrimly app. Took a few rounds to track down — the FCM plugin was failing to load at runtime because of a build-time import quirk that masked everything downstream. Fixed and verified end to end. iOS push comes after we wrap the Apple Developer Program registration.',
    tags: ['Push', 'Android', 'New Feature'],
  },
  {
    id: -8,
    version: 'v2.0.8',
    date: 'May 19, 2026',
    type: 'improvement',
    title: 'Roster Reachability Badges',
    description:
      'Each parent row on the roster now shows three small badges — 📱 App, 💬 SMS, 📧 Email — so coaches can see at a glance which channels will actually reach which parent before sending a broadcast. Green means reachable, gray means not set up yet, amber means a phone is on file but the parent hasn\'t opted in to SMS.',
    tags: ['Roster', 'Coaches', 'New Feature'],
  },
  {
    id: -7,
    version: 'v2.0.7',
    date: 'May 19, 2026',
    type: 'improvement',
    title: 'Parent Invite: Sign In Option',
    description:
      'Parents who already have a Scrimly account (re-invited after a roster delete, or coaches who happen to be parents of their own kids) can now tap "Sign in" from an invite link instead of getting stuck on a dead-end "email already in use" error. Both the sign-up and sign-in paths preserve the invite code, so once authenticated the user lands back on the accept page automatically.',
    tags: ['Invites', 'Parents', 'Fix'],
  },
  {
    id: -6,
    version: 'v2.0.6',
    date: 'May 5, 2026',
    type: 'major',
    title: 'Start a Direct Message',
    description:
      'Tap "New Message" from your inbox to start a 1:1 chat with anyone on your team. The picker shows coaches and parents you share a team with, sorted with coaches first. Already chatted with someone? Picking them again opens your existing thread instead of creating a duplicate. This wraps up the chat MVP: announcements, DMs, real-time delivery, push notifications, image attachments, all live.',
    tags: ['Chat', 'New Feature', 'MVP Complete'],
  },
  {
    id: -5,
    version: 'v2.0.5',
    date: 'May 5, 2026',
    type: 'major',
    title: 'Image Attachments in Chat',
    description:
      'Tap the paperclip to attach a photo from your gallery, or the camera icon on mobile to take one fresh. Big iPhone photos get compressed to ~500KB before upload so you don\'t eat anyone\'s data plan. Add an optional caption, see a live progress bar, and the image renders inline in the thread for everyone in the chat. Caps: 30MB raw input, images only.',
    tags: ['Chat', 'New Feature', 'Attachments'],
  },
  {
    id: -4,
    version: 'v2.0.4',
    date: 'May 5, 2026',
    type: 'major',
    title: 'Chat Send is Live',
    description:
      'You can post messages now. Coaches can send announcements to their team channel; anyone in a direct message can chat back and forth. Auto-grow textarea, Enter to send (Shift+Enter for new line on desktop), and the new message appears in real time across every device viewing the chat. Push notifications fire automatically to participants who aren\'t looking at the screen. Image attachments come next.',
    tags: ['Chat', 'New Feature'],
  },
  {
    id: -3,
    version: 'v2.0.3',
    date: 'May 5, 2026',
    type: 'major',
    title: 'Chat Threads Live (Read-Only)',
    description:
      'Tap any chat in your inbox to open the full conversation. Messages render with sender avatars, timestamps, day dividers, and your messages on the right (lime) vs others on the left (white). Image messages show inline thumbnails. Auto mark-as-read keeps your inbox badges accurate. Sending messages comes next phase.',
    tags: ['Chat', 'New Feature'],
  },
  {
    id: -2,
    version: 'v2.0.2',
    date: 'May 5, 2026',
    type: 'major',
    title: 'New Chat Inbox',
    description:
      'The Messages page is back. You\'ll see your team announcement channel and any direct messages, sorted by most recent. Filter by All or Unread. Each team automatically has an announcement channel (coaches post, parents read). Reading the actual messages and posting new ones lands in the next two phases over the coming days.',
    tags: ['Chat', 'New Feature'],
  },
  {
    id: -1,
    version: 'v2.0.1',
    date: 'May 5, 2026',
    type: 'improvement',
    title: 'Chat Rebuild Started',
    description:
      'Pulled the old chat (which had three competing implementations stacked from the pivot) and started rebuilding clean. Coming back over the next 2-3 weeks with: announcement-style team channel, coach-to-parent direct messages, image attachments, real-time delivery, push notifications, and per-chat mute. All free across all tiers. Use the bilingual broadcast tool for now to reach your roster.',
    tags: ['Chat', 'Rebuild'],
  },
  {
    id: 0,
    version: 'v2.0.0',
    date: 'May 4, 2026',
    type: 'major',
    title: 'Private Beta Launch',
    description:
      'Scrimly relaunched as a full club & team management platform. Beta-tester welcome screen, floating bug button for direct feedback to Kurt, beta perks (Team tier free for 1 year, 50% off for 2 more years), and a refreshed lime and pitch design system across the app. The original scrimmage-matching tool is being rebuilt as a feature inside Scrimly later this year.',
    tags: ['Launch', 'Beta', 'Design'],
  },
  {
    id: 1,
    version: 'v1.5.0',
    date: 'Apr 26, 2026',
    type: 'major',
    title: 'New Club Tier and Founding Club Program',
    description:
      'Added a $99/mo Club tier covering 3 teams, fixing the gap between solo coaches and full clubs. Launched the Founding Club program: first 5 clubs get the $349/mo Growth tier locked at $199/mo for 3 years, free migration, Stripe at-cost forever, and weekly check-ins.',
    tags: ['Pricing', 'Founding Club'],
  },
  {
    id: 2,
    version: 'v1.4.0',
    date: 'Apr 13, 2026',
    type: 'major',
    title: 'Quick Start Bar and Message Memory',
    description:
      'Tap Reminder, Game Recap, Schedule Change, or Payment Due right from the Messages page or Dashboard. Scrimly pre-fills the form with what you entered last time, so updating a weekly practice reminder takes under 30 seconds.',
    tags: ['New Feature', 'AI Messages', 'UX'],
  },
  {
    id: 3,
    version: 'v1.3.0',
    date: 'Apr 5, 2026',
    type: 'major',
    title: 'WhatsApp One-Tap Sharing',
    description:
      'Every generated message has a green WhatsApp button. Tap it and your message, in English or Spanish, is pre-loaded in WhatsApp. Pick the group and hit Send. No copy and paste needed.',
    tags: ['New Feature', 'WhatsApp', 'Sharing'],
  },
  {
    id: 4,
    version: 'v1.2.0',
    date: 'Mar 22, 2026',
    type: 'major',
    title: 'AI Message Generator: 8 Types',
    description:
      'Generate professional bilingual messages for any occasion: Game Recap, Reminder, Schedule Change, Payment Due, Cancellation, Motivation, Tryout Info, and Season Wrap. Every message is written in English and Spanish simultaneously.',
    tags: ['New Feature', 'AI', 'Bilingual'],
  },
  {
    id: 5,
    version: 'v1.1.2',
    date: 'Mar 10, 2026',
    type: 'improvement',
    title: 'Roster CSV Import',
    description:
      'Add your whole team at once by uploading a CSV. We auto-detect name, jersey number, and position columns. No template required.',
    tags: ['Improvement', 'Roster'],
  },
  {
    id: 6,
    version: 'v1.1.0',
    date: 'Feb 28, 2026',
    type: 'major',
    title: 'Season Registration Pages',
    description:
      'Create a public registration page for any season in minutes. Parents fill out the form, you see submissions in real time. No Google Forms, no spreadsheets.',
    tags: ['New Feature', 'Seasons'],
  },
  {
    id: 7,
    version: 'v1.0.0',
    date: 'Feb 1, 2026',
    type: 'major',
    title: 'Scrimly Launch',
    description:
      'Scrimly went live. Build your team, add your roster, and send your first AI-generated bilingual message to parents, all for free.',
    tags: ['Launch'],
  },
]

// ----------------------------------------------------------------
// What's coming next, read-only roadmap
// ----------------------------------------------------------------
const roadmapItems = [
  {
    id: 'scrimmage-matching',
    icon: '⚽',
    title: 'Scrimmage Matching Returns',
    status: 'Q3 2026',
    statusTone: 'building',
    description:
      'The original scrimmage-finder is being rebuilt as a feature inside Scrimly. Coming back with the team density to actually make it instant.',
  },
  {
    id: 'native-ios',
    icon: '📱',
    title: 'Native iOS App',
    status: 'Coming Soon',
    statusTone: 'next',
    description:
      'Android is launching first on the Play Store. iOS native build follows shortly after, same features, same login.',
  },
  {
    id: 'scheduled-send',
    icon: '⏰',
    title: 'Scheduled Message Send',
    status: 'Planned',
    statusTone: 'planned',
    description:
      'Write a game-day reminder Monday, schedule it to send Friday morning. Set it and forget it.',
  },
  {
    id: 'self-serve-billing',
    icon: '💳',
    title: 'Self-Serve Subscriptions',
    status: 'Phase 3',
    statusTone: 'planned',
    description:
      'Pick a tier, pay with a card, switch plans yourself. No more emails to Kurt for billing changes.',
  },
]

// ----------------------------------------------------------------
// Status pill helper
// ----------------------------------------------------------------
function statusClasses(tone) {
  if (tone === 'building') {
    return 'bg-[#b5f03d] text-[#0d1f14]'
  }
  if (tone === 'next') {
    return 'bg-[#b5f03d]/20 text-[#b5f03d] ring-1 ring-[#b5f03d]/40'
  }
  return 'bg-white/10 text-white/70 ring-1 ring-white/15'
}

// ----------------------------------------------------------------
// UpdateCard, vertical timeline entry
// ----------------------------------------------------------------
function UpdateCard({ update, isLast }) {
  const isMajor = update.type === 'major'
  const Icon = isMajor ? SparklesIcon : WrenchScrewdriverIcon

  return (
    <li className="relative pl-8 sm:pl-12">
      {/* Vertical rail */}
      {!isLast && (
        <span
          aria-hidden="true"
          className="absolute left-3 sm:left-5 top-8 bottom-0 w-px bg-zinc-200"
        />
      )}

      {/* Timeline dot */}
      <span
        aria-hidden="true"
        className={[
          'absolute left-0 sm:left-2 top-2 inline-flex items-center justify-center h-6 w-6 sm:h-7 sm:w-7 rounded-full ring-4 ring-[#f7faf5]',
          isMajor ? 'bg-[#b5f03d]' : 'bg-zinc-300',
        ].join(' ')}
      >
        <Icon className={['h-3.5 w-3.5', isMajor ? 'text-[#0d1f14]' : 'text-zinc-600'].join(' ')} />
      </span>

      <div className="pb-10">
        {/* Date and version */}
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          <span
            className="text-[11px] font-bold uppercase tracking-[1.2px] text-[#5a8a1f]"
            style={{ fontFamily: HEAD_FONT }}
          >
            {update.date}
          </span>
          <span className="text-zinc-300">·</span>
          <span className="text-xs font-bold text-zinc-500 font-mono">{update.version}</span>
          {isMajor ? (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 text-[#5a8a1f] px-2 py-0.5 text-[10px] font-bold uppercase"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              <SparklesIcon className="h-3 w-3" />
              Spotlight
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-zinc-100 text-zinc-600 px-2 py-0.5 text-[10px] font-bold uppercase"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              <WrenchScrewdriverIcon className="h-3 w-3" />
              Improvement
            </span>
          )}
        </div>

        {/* Card */}
        <div
          className={[
            'rounded-2xl border bg-white p-5 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] transition-all',
            isMajor ? 'border-[#b5f03d]/30' : 'border-zinc-200',
          ].join(' ')}
        >
          <h3
            className="text-lg font-bold uppercase text-[#0d1f14] mb-2 leading-tight"
            style={{ fontFamily: HEAD_FONT }}
          >
            {update.title}
          </h3>
          <p className="text-sm text-zinc-600 leading-relaxed mb-4">{update.description}</p>
          <div className="flex flex-wrap gap-1.5">
            {update.tags.map((tag, i) => (
              <span
                key={i}
                className="text-[10px] font-bold text-zinc-500 uppercase tracking-wide border border-zinc-200 bg-white px-2 py-0.5 rounded"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </li>
  )
}

// ----------------------------------------------------------------
// RoadmapCard, read-only "coming up" tile
// ----------------------------------------------------------------
function RoadmapCard({ item }) {
  return (
    <div className="flex flex-col p-5 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 hover:ring-[#b5f03d]/30 transition-colors">
      <div className="flex justify-between items-start mb-4">
        <div className="text-3xl">{item.icon}</div>
        <span
          className={['inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase', statusClasses(item.statusTone)].join(' ')}
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
        >
          {item.statusTone === 'building' && <RocketLaunchIcon className="h-3 w-3" />}
          {item.statusTone === 'planned' && <ClockIcon className="h-3 w-3" />}
          {item.status}
        </span>
      </div>

      <h3
        className="text-white font-bold text-lg mb-2 uppercase leading-tight"
        style={{ fontFamily: HEAD_FONT }}
      >
        {item.title}
      </h3>
      <p className="text-white/60 text-sm leading-relaxed flex-grow">{item.description}</p>
    </div>
  )
}

// ----------------------------------------------------------------
// SuggestionCard, writes to the real `feedback` collection
// ----------------------------------------------------------------
function FeatureSuggestionCard() {
  const { firebaseUser, userProfile } = useAuth()
  const [suggestion, setSuggestion] = useState('')
  const [status, setStatus] = useState('idle')

  const handleSubmit = async (e) => {
    e.preventDefault()
    const trimmed = suggestion.trim()
    if (!trimmed) return
    setStatus('loading')

    try {
      await addDoc(collection(db, 'feedback'), {
        type: 'feature',
        message: trimmed,
        pageUrl: typeof window !== 'undefined' ? window.location.href : '',
        pagePath: '/changelog',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        viewport: typeof window !== 'undefined'
          ? `${window.innerWidth}x${window.innerHeight}`
          : '',
        userId: firebaseUser?.uid || null,
        userEmail: firebaseUser?.email || userProfile?.email || null,
        displayName: userProfile?.displayName || firebaseUser?.displayName || null,
        role: userProfile?.role || null,
        source: 'changelog_suggestion_form',
        createdAt: serverTimestamp(),
      })
      setStatus('success')
      setSuggestion('')
      toast.success('Got it. Kurt sees every suggestion.')
    } catch (err) {
      console.error('[Changelog] suggestion submit failed', err)
      toast.error('Could not save. Try the bug button at the bottom-right.')
      setStatus('idle')
    }
  }

  if (status === 'success') {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 rounded-2xl bg-white/[0.04] ring-1 ring-dashed ring-[#b5f03d]/40 text-center">
        <div className="w-12 h-12 rounded-full bg-[#b5f03d]/15 ring-2 ring-[#b5f03d]/40 flex items-center justify-center mb-4">
          <CheckIcon className="h-6 w-6 text-[#b5f03d]" />
        </div>
        <h3
          className="text-white font-bold uppercase mb-2"
          style={{ fontFamily: HEAD_FONT }}
        >
          Suggestion Received
        </h3>
        <p className="text-white/60 text-sm">Kurt reads every single one.</p>
        <button
          onClick={() => setStatus('idle')}
          className="mt-6 text-xs font-bold uppercase tracking-wide text-[#b5f03d] hover:text-white"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
        >
          Submit Another Idea →
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col p-5 rounded-2xl bg-white/[0.04] ring-1 ring-dashed ring-white/15 hover:ring-white/25 transition-colors h-full">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
          <ChatBubbleLeftRightIcon className="h-5 w-5 text-[#b5f03d]" />
        </div>
        <h3
          className="text-white font-bold text-lg uppercase"
          style={{ fontFamily: HEAD_FONT }}
        >
          Got an Idea?
        </h3>
      </div>
      <p className="text-white/60 text-sm mb-4">
        Don't see what you need? Tell us what would make your coaching life easier.
      </p>
      <form onSubmit={handleSubmit} className="flex-grow flex flex-col">
        <textarea
          value={suggestion}
          onChange={(e) => setSuggestion(e.target.value)}
          placeholder="I wish Scrimly could..."
          className="flex-grow w-full bg-[#0a1410] border border-white/10 rounded-xl p-3 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#b5f03d]/40 focus:border-[#b5f03d] mb-3 resize-none min-h-[100px]"
        />
        <button
          type="submit"
          disabled={!suggestion.trim() || status === 'loading'}
          className="w-full py-3 rounded-xl text-sm font-bold uppercase flex items-center justify-center gap-2 bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] transition-all disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.97]"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
        >
          {status === 'loading'
            ? <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Sending...</>
            : <><PaperAirplaneIcon className="h-4 w-4" /> Submit Idea</>
          }
        </button>
      </form>
    </div>
  )
}

// ----------------------------------------------------------------
// Main page
// ----------------------------------------------------------------
export default function ChangelogPage() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-[#f7faf5] pb-20">

      {/* Mobile back */}
      <div className="md:hidden pt-6 px-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-[#5a8a1f] hover:text-[#3d6010]"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </button>
      </div>

      {/* Header */}
      <div className="pt-10 md:pt-16 pb-12 md:pb-16 px-6 text-center max-w-2xl mx-auto">
        <p
          className="text-xs font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-3"
          style={{ fontFamily: HEAD_FONT }}
        >
          Changelog
        </p>
        <h1
          className="text-4xl md:text-5xl font-bold uppercase text-[#0d1f14] mb-4 tracking-tight leading-none"
          style={{ fontFamily: HEAD_FONT }}
        >
          What's <span className="text-[#5a8a1f]">New.</span>
        </h1>
        <p className="text-zinc-500 text-base sm:text-lg leading-relaxed">
          Every update we ship is built to save you time and help you communicate better with your families.
        </p>
      </div>

      {/* Past updates timeline */}
      <div className="max-w-3xl mx-auto px-6 mb-20">
        <div className="flex items-center gap-3 mb-8">
          <h2
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] whitespace-nowrap"
            style={{ fontFamily: HEAD_FONT }}
          >
            Recent Releases
          </h2>
          <div className="h-px bg-zinc-200 flex-grow" />
        </div>
        <ul className="relative">
          {pastUpdates.map((u, i) => (
            <UpdateCard key={u.id} update={u} isLast={i === pastUpdates.length - 1} />
          ))}
        </ul>
      </div>

      {/* Coming up, pitch-dark section with lime accents */}
      <div
        className="relative overflow-hidden bg-[#0d1f14] py-16 md:py-20 px-6"
        style={{
          backgroundImage:
            'linear-gradient(rgba(181,240,61,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(181,240,61,0.05) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      >
        {/* Soft glow */}
        <div className="absolute top-0 right-0 -mr-32 -mt-32 w-96 h-96 rounded-full bg-[#b5f03d]/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 -ml-32 -mb-32 w-96 h-96 rounded-full bg-[#b5f03d]/5 blur-3xl" />

        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#b5f03d]/10 ring-1 ring-[#b5f03d]/30 mb-5"
            >
              <RocketLaunchIcon className="h-3.5 w-3.5 text-[#b5f03d]" />
              <span
                className="text-[10px] font-bold tracking-[1.4px] uppercase text-[#b5f03d]"
                style={{ fontFamily: HEAD_FONT }}
              >
                Roadmap
              </span>
            </div>
            <h2
              className="text-3xl md:text-4xl font-bold uppercase text-white mb-4 leading-none"
              style={{ fontFamily: HEAD_FONT }}
            >
              What's Coming <span className="text-[#b5f03d]">Next.</span>
            </h2>
            <p className="text-white/60 text-base sm:text-lg">
              The shortlist of what we're building. Got an idea that should be on this list? Tell us below.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {roadmapItems.map((item) => (
              <RoadmapCard key={item.id} item={item} />
            ))}
            <FeatureSuggestionCard />
          </div>

          {/* Direct line to Kurt callout, mirrors the welcome modal CTA */}
          <div className="mt-12 max-w-xl mx-auto text-center rounded-2xl bg-white/[0.04] ring-1 ring-white/10 px-6 py-5">
            <p className="text-sm text-white/70">
              <span className="text-[#b5f03d] font-bold">Found a bug?</span> Tap the bug icon in the bottom-right corner of any page. It goes straight to Kurt.
            </p>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-1.5 mt-3 text-xs font-bold uppercase tracking-wide text-[#b5f03d] hover:text-white"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              Back to Dashboard →
            </Link>
          </div>
        </div>
      </div>

    </div>
  )
}
